import { embed, cosineSimilarity, generateObject } from "ai";
import { createGoogleGenerativeAI } from "@ai-sdk/google";
import { model } from "@builder/assist/services/useAiChat";
import { z } from "zod";

export interface ResourceItem<ResourceType> {
    id: string;
    value: ResourceType;
    description: string;
    metadata?: Record<string, any>;
  }
  
  export interface VectorizedItem<ResourceType> extends ResourceItem<ResourceType> {
    vector: number[];
  }
  
  

// not importing the Model from useAiChat because might have a different embedding model
const googleAI = createGoogleGenerativeAI({
  apiKey: import.meta.env?.VITE_GOOGLE_API_KEY
});

export interface SearchOptions<ResourceType> {
  numberOfResults?: number;
  threshold?: number;
  filter?: (item: VectorizedItem<ResourceType>) => boolean;
}

export interface SearchResult<ResourceType> {
  id: string;
  value: ResourceType;
  description: string;
  metadata?: Record<string, any>;
  score: number;
}

// cache results irrespective of resource type (vectorized or not)
const cache = new Map<string, any[]>();

// Preload all JSON modules within this folder (and subfolders) so Vite can bundle them properly
const jsonModules = import.meta.glob<{ default: any }>("./**/*.json");

async function _loadResourceFile<ItemType>(relativePath: string): Promise<ItemType[]> {
  // Normalize path → ensure it ends with .json and starts with ./ so it matches the keys generated by import.meta.glob
  const normalized = relativePath.endsWith(".json") ? relativePath : `${relativePath}.json`;
  const key = normalized.startsWith("./") ? normalized : `./${normalized}`;

  // Check cache first
  if (cache.has(key)) {
    return cache.get(key)! as ItemType[];
  }

  const loader = jsonModules[key];
  if (!loader) {
    throw new Error(`Resource file not found or not included in bundle: ${key}`);
  }

  const mod = await loader();
  const items = (mod.default ?? mod) as ItemType[];

  cache.set(key, items);
  return items;
}

export async function searchVectorData<ResultType>(
  relativePath: string,
  query?: string,
  options: SearchOptions<ResultType> = {}
): Promise<SearchResult<ResultType>[]> {
  const items = (await _loadResourceFile<VectorizedItem<ResultType>>(relativePath)) as VectorizedItem<
    ResultType
  >[];
  
  let filteredItems = items;
  
  if (options.filter) {
    filteredItems = filteredItems.filter(options.filter as any);
  }
  
  // If no query, return all filtered items (no embedding needed)
  if (!query) {
    const results = filteredItems
      .map((item) => ({
        id: item.id,
        value: item.value,
        description: item.description,
        metadata: item.metadata,
        score: 1.0,
      }))
      .slice(0, options.numberOfResults || 20);
    
    return results as SearchResult<ResultType>[];
  }
  
  // Use semantic search with embedding
  const { numberOfResults = 5, threshold = 0.1 } = options;
  
  const { embedding } = await embed({
    model: googleAI.textEmbeddingModel("text-embedding-004"),
    value: query,
  });
  
  const results = filteredItems
    .map((item) => ({
      id: item.id,
      value: item.value,
      description: item.description,
      metadata: item.metadata,
      score: cosineSimilarity(embedding, item.vector),
    }))
    .filter((result) => result.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, numberOfResults);
  
  return results as SearchResult<ResultType>[];
}

/**
 * Better for small datasets that don't need to be vectorized.
 */
export async function searchByDescription<ResultType>(
  relativePath: string,
  query: string,
  options: SearchOptions<ResultType> = {}
): Promise<SearchResult<ResultType>[]> {
  const items = (await _loadResourceFile<ResourceItem<ResultType>>(relativePath)) as ResourceItem<
    ResultType
  >[];

  let filteredItems: ResourceItem<ResultType>[] = items;
  if (options.filter) {
    filteredItems = filteredItems.filter(options.filter as any) as ResourceItem<ResultType>[];
  }

  if (!query) {
    return filteredItems
      .map((item) => ({
        id: item.id,
        value: item.value,
        description: item.description,
        metadata: item.metadata,
        score: 1.0,
      }))
      .slice(0, options.numberOfResults || 20) as SearchResult<ResultType>[];
  }

  const { numberOfResults = 1 } = options;

  if (filteredItems.length === 0) return [];

  // Build a "string enum" dynamically so the model can only return valid IDs
  const ids = filteredItems.map((item) => item.id) as [string, ...string[]];
  const idsEnum = z.enum(ids);

  const schema = z.object({
    relevantIds: z.array(idsEnum).describe("List of item IDs that are most relevant to the user's query, ordered by relevance (most relevant first). Return an empty array if none are relevant."),
  });

  const itemsListText = filteredItems
    .map((item) => `ID: ${item.id}\nDESCRIPTION: ${item.description}`)
    .join("\n---\n");


  const { object: resultObject } = await generateObject({
    model,
    schema,
    prompt: `
      "instruction": "Find the most relevant items based on the query: ${query}",
      "availableItems": ${itemsListText},
      "numberOfResults": ${numberOfResults}
      "Respond with a JSON object that matches the schema."
    `,
  });

  const relevantIds: string[] = (resultObject as any).relevantIds ?? [];

  const results: SearchResult<ResultType>[] = [];
  relevantIds.slice(0, numberOfResults).forEach((id) => {
    const item = filteredItems.find((i) => i.id === id);
    if (item) {
      results.push({
        id: item.id,
        value: item.value,
        description: item.description,
        metadata: item.metadata,
        score: 1.0,
      });
    }
  });
  console.group("searchByDescription results");
  console.log("relativePath", relativePath);
  console.log("query", query);
  console.log("options", options);
  console.log("results", results);
  console.groupEnd();

  return results;
}

